/**
 * Markdown 编辑器组件
 * 提供基础的 Markdown 编辑功能
 */
import React, { useRef, useEffect, useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

interface MarkdownEditorProps {
  value?: string
  onChange?: (value: string) => void
  onSave?: (value: string) => void
  placeholder?: string
  className?: string
  readOnly?: boolean
}

/**
 * Markdown 编辑器组件
 * 支持基本的 Markdown 编辑功能，包括 Tab 键缩进、高度自适应等
 */
const EDITOR_MIN_HEIGHT = 200
const HEIGHT_THRESHOLD = 10
const TAB_SPACES = '  '

export const MarkdownEditor: React.FC<MarkdownEditorProps> = ({
  value = '',
  onChange,
  onSave,
  placeholder,
  className = '',
  readOnly = false
}) => {
  const { t } = useTranslation()
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const [currentHeight, setCurrentHeight] = useState(EDITOR_MIN_HEIGHT)

  // Use i18n placeholder if not provided
  const placeholderText = placeholder || t('editor.placeholder')

  // 处理输入变化
  const handleChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value
    if (onChange) {
      onChange(newValue)
    }
  }, [onChange])

  // 处理键盘事件
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (readOnly) return

    // Tab 键处理
    if (e.key === 'Tab') {
      e.preventDefault()
      const textarea = textareaRef.current
      if (!textarea) return

      const start = textarea.selectionStart
      const end = textarea.selectionEnd
      const currentValue = textarea.value

      // 插入空格作为 Tab
      const newValue = currentValue.substring(0, start) + TAB_SPACES + currentValue.substring(end)
      
      // 更新值并恢复光标位置
      if (onChange) {
        onChange(newValue)
      }

      // 使用 requestAnimationFrame 优化光标位置恢复
      requestAnimationFrame(() => {
        textarea.selectionStart = textarea.selectionEnd = start + TAB_SPACES.length
        textarea.focus()
      })
    }

    // Ctrl/Cmd + S 保存
    if ((e.ctrlKey || e.metaKey) && e.key === 's') {
      e.preventDefault()
      if (onSave) {
        onSave(value)
      }
    }
  }, [readOnly, onChange, onSave, value])

  // 自动调整文本域高度（优化版本）
  const adjustHeight = useCallback(() => {
    const textarea = textareaRef.current
    if (!textarea) return

    // 获取内容所需高度
    const contentHeight = Math.max(textarea.scrollHeight, EDITOR_MIN_HEIGHT)

    // 只有当高度变化超过阈值时才更新，避免频繁调整
    if (Math.abs(contentHeight - currentHeight) > HEIGHT_THRESHOLD) {
      setCurrentHeight(contentHeight)
      // 直接设置新高度，避免先重置为auto再设置的过程
      textarea.style.height = `${contentHeight}px`
    }
  }, [currentHeight])

  // 当内容变化时调整高度
  useEffect(() => {
    adjustHeight()
  }, [value]) // adjustHeight 是用useCallback包装的，依赖稳定

  // 确保编辑器获得焦点
  useEffect(() => {
    const textarea = textareaRef.current
    if (textarea && !readOnly) {
      textarea.focus()
    }
  }, [readOnly])

  // 监听窗口大小变化，重新调整高度
  useEffect(() => {
    const handleResize = () => {
      adjustHeight()
    }

    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [adjustHeight])

  return (
    <div className={`markdown-editor relative h-full ${className}`}>
      <textarea
        id="markdown-editor"
        name="content"
        ref={textareaRef}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholderText}
        readOnly={readOnly}
        className={`
          w-full p-0 bg-transparent border-none outline-none resize-none overflow-hidden
          text-gray-900 dark:text-gray-100 font-mono text-sm leading-relaxed
          placeholder-gray-400 dark:placeholder-gray-500
          focus:outline-none
          ${readOnly ? 'cursor-default' : 'cursor-text'}
        `}
        style={{
          lineHeight: '1.6',
          minHeight: '100%'
        }}
        spellCheck={false}
      />
      
      {!readOnly && (
        <div className="absolute bottom-2 right-2 pointer-events-none">
          <div className="text-xs text-gray-400 dark:text-gray-500 bg-white/80 dark:bg-gray-900/80 px-2 py-1 rounded-md backdrop-blur-sm">
            {t('editor.characterCount', { count: value.length })}
          </div>
        </div>
      )}
    </div>
  )
}